/**
 * Video CTA Configuration
 * Channel Invite (Telegram Groups) - principal. Config desde telegramGroupsVideo.
 */

import type { StaticPageVideoPage } from "./staticPageVideoConfig";
import {
  TELEGRAM_GROUPS_PLAYBACK_ID,
  TELEGRAM_GROUPS_CTA_URL,
  getTelegramGroupsVideoConfig,
  hasChannelInviteVideo,
} from "@/config/telegramGroupsVideo";

export type VideoCTAService =
  | "telegram"
  | "channel_invite"
  | "telegram_groups"
  | "custom";

export type SupportedLanguage = "en" | "es" | "de" | "fr" | "it" | "pt" | "nl";

/** Resolución admin + env (`resolveMuxFriendlyFunnelOpts`). */
export type MuxFriendlyFunnelOpts = {
  friendlyBanner: "on" | "off" | "unset";
  freeJoinCopy: boolean;
};

/** One Railway flag: CTA copy tuned for “join the free Telegram” funnel. */
export function readMuxCtaFreeJoinCopyEnvActive(): boolean {
  const raw = (import.meta.env.VITE_MUX_CTA_FREE_JOIN_COPY as string | undefined)
    ?.toLowerCase()
    .trim();
  return raw === "true" || raw === "1" || raw === "yes" || raw === "on";
}

function isMuxCtaFreeJoinCopyActive(funnel?: MuxFriendlyFunnelOpts | null): boolean {
  return funnel?.freeJoinCopy ?? readMuxCtaFreeJoinCopyEnvActive();
}

/** Líneas del cartel “no es VIP” (Mux CTA / overlay Mi VIP). */
const MUX_FRIENDLY_BANNER_FREE: Record<SupportedLanguage, string> = {
  es: "Podés entrar al canal gratis, con todo el cariño. No es el VIP de pago — un toque y listo.",
  en: "You can join the free channel — warmly, no pressure. Not paid VIP — one tap and you’re in.",
  de: "Du kannst dem Gratis-Kanal beitreten — ganz entspannt. Nicht der bezahlte VIP — ein Tipp genügt.",
  fr: "Tu peux rejoindre le canal gratuit, en douceur. Ce n’est pas le VIP payant — un tap et c’est parti.",
  it: "Puoi entrare nel canale gratis, con calore. Non è il VIP a pagamento — un tocco e ci sei.",
  pt: "Você pode entrar no canal grátis, com carinho. Não é o VIP pago — um toque e pronto.",
  nl: "Je mag gratis bij de groep — zonder stress. Geen betaalde VIP — één tik en je bent binnen.",
};

const MUX_FRIENDLY_BANNER_SOFT: Record<SupportedLanguage, string> = {
  es: "Esto es el espacio de la comunidad (gratis), no el plan VIP pago. El siguiente paso está abajo, con buena onda.",
  en: "This is the free community space — not paid VIP. Next step is below, nice and easy.",
  de: "Das ist der kostenlose Community-Bereich — nicht der bezahlte VIP. Der nächste Schritt steht unten, ganz relaxed.",
  fr: "Ici c’est l’espace communautaire gratuit — pas le VIP payant. L’étape suivante est en bas, tout en douceur.",
  it: "Qui c’è lo spazio community gratuito — non il VIP a pagamento. Il passo dopo è sotto, con calma.",
  pt: "Aqui é o espaço da comunidade grátis — não o VIP pago. O próximo passo está abaixo, com leveza.",
  nl: "Dit is de gratis community — niet de betaalde VIP. De volgende stap staat hieronder, rustig.",
};

export function readMuxCtaFriendlyBannerEnv(): "on" | "off" | "unset" {
  const raw = (import.meta.env.VITE_MUX_CTA_FRIENDLY_BANNER as string | undefined)
    ?.toLowerCase()
    .trim();
  if (raw === "true" || raw === "1" || raw === "yes" || raw === "on") return "on";
  if (raw === "false" || raw === "0" || raw === "no" || raw === "off")
    return "off";
  return "unset";
}

/**
 * `VITE_MUX_CTA_FRIENDLY_BANNER=false` (u off/0/no) — bloquea cartel + funnel auto por URL en premium simple.
 * Si pasás `funnel` (admin + env ya resueltos), usa ese estado.
 */
export function isMuxCtaFriendlyBannerEnvStrictOff(
  funnel?: MuxFriendlyFunnelOpts | null,
): boolean {
  const b = funnel?.friendlyBanner ?? readMuxCtaFriendlyBannerEnv();
  return b === "off";
}

/**
 * Texto breve encima del CTA en `MuxVideoPlayerWithCTA` (tono suave).
 * Deja explícito que **no es el plan VIP** (acceso canal / comunidad / grupo).
 * Incluye página estática `grupo-privado` aunque no venga `service`.
 *
 * **Cuándo se muestra:** con `VITE_MUX_CTA_FRIENDLY_BANNER=true` (o 1/on/yes), **o** si
 * `VITE_MUX_CTA_FREE_JOIN_COPY` está activo (mensaje sin cargo). Si no ponés ninguna de
 * las dos, no hay cartel. Con `FRIENDLY_BANNER=false` se oculta el cartel **salvo** que
 * `FREE_JOIN_COPY` esté activo (sigue el aviso sin cargo).
 * En overlay Mi VIP (`premiumSimpleMembershipFreeFunnel`) el cartel puede mostrarse aunque
 * `FRIENDLY_BANNER` no esté en `on`, salvo `FRIENDLY_BANNER=false` explícito sin `FREE_JOIN_COPY`.
 * Si el padre pasa `ctaSubtitle` y no es `premiumSimpleMembershipFreeFunnel`, no se muestra (evita duplicar).
 */
export function getMuxCtaFriendlyBannerMessage(
  lang: SupportedLanguage,
  ctx: {
    service?: VideoCTAService;
    ctaSubtitle?: string;
    staticPage?: StaticPageVideoPage | null;
    /**
     * Overlay “Mi VIP” en BioTemplatePremiumSimple cuando el CTA abre Telegram gratis:
     * muestra el aviso “no es VIP” sin exigir `service`/`staticPage` ni tapar el cartel con `ctaSubtitle`.
     */
    premiumSimpleMembershipFreeFunnel?: boolean;
  },
  funnel?: MuxFriendlyFunnelOpts | null,
): string | null {
  if (ctx.ctaSubtitle?.trim() && !ctx.premiumSimpleMembershipFreeFunnel)
    return null;

  if (ctx.premiumSimpleMembershipFreeFunnel) {
    const freeJoin = isMuxCtaFreeJoinCopyActive(funnel);
    const bannerEnv = funnel?.friendlyBanner ?? readMuxCtaFriendlyBannerEnv();
    if (bannerEnv === "off" && !freeJoin) return null;
    const lines = freeJoin ? MUX_FRIENDLY_BANNER_FREE : MUX_FRIENDLY_BANNER_SOFT;
    return lines[lang] || lines.en;
  }

  const svc = ctx.service;
  const staticPage = ctx.staticPage ?? null;
  const isTelegramLike =
    staticPage === "grupo-privado" ||
    svc === "telegram" ||
    svc === "telegram_groups" ||
    svc === "channel_invite";
  if (!isTelegramLike) return null;

  const freeJoin = isMuxCtaFreeJoinCopyActive(funnel);
  const bannerEnv = funnel?.friendlyBanner ?? readMuxCtaFriendlyBannerEnv();
  if (bannerEnv === "off" && !freeJoin) return null;

  if (!freeJoin && bannerEnv !== "on") return null;

  const lines = freeJoin ? MUX_FRIENDLY_BANNER_FREE : MUX_FRIENDLY_BANNER_SOFT;
  return lines[lang] || lines.en;
}

export interface VideoCTAConfig {
  playbackId: string;
  ctaUrl: string;
  ctaLabel?: Record<SupportedLanguage, string>;
  title?: Record<SupportedLanguage, string>;
  playbackIdByCountry?: Record<string, string>;
  ctaUrlByCountry?: Record<string, string>;
}

/**
 * Get default video CTA configuration for a service
 * Solo channel_invite (telegram groups) - lo principal
 */
function getDefaultVideoConfig(service: VideoCTAService): VideoCTAConfig {
  const telegramConfig = getTelegramGroupsVideoConfig();

  switch (service) {
    case "telegram":
    case "telegram_groups":
    case "channel_invite": {
      const freeJoin = isMuxCtaFreeJoinCopyActive(null);
      return {
        playbackId: TELEGRAM_GROUPS_PLAYBACK_ID,
        ctaUrl: TELEGRAM_GROUPS_CTA_URL,
        playbackIdByCountry: telegramConfig.playbackIds,
        ctaUrlByCountry: telegramConfig.ctaUrls,
        ctaLabel: freeJoin
          ? {
              en: "Join free",
              es: "Únete gratis",
              de: "Kostenlos beitreten",
              fr: "Rejoindre gratuitement",
              it: "Iscriviti gratis",
              pt: "Entrar grátis",
              nl: "Gratis deelnemen",
            }
          : {
              en: "Exclusive Member",
              es: "Miembro Exclusivo",
              de: "Exklusives Mitglied",
              fr: "Membre Exclusif",
              it: "Membro Esclusivo",
              pt: "Membro Exclusivo",
              nl: "Exclusief Lid",
            },
        title: freeJoin
          ? {
              en: "Free access (not VIP)",
              es: "Acceso gratuito (no es VIP)",
              de: "Kostenloser Zugang (nicht VIP)",
              fr: "Accès gratuit (pas VIP)",
              it: "Accesso gratuito (non è VIP)",
              pt: "Acesso gratuito (não é VIP)",
              nl: "Gratis toegang (geen VIP)",
            }
          : {
              en: "Exclusive community (not the VIP plan)",
              es: "Comunidad exclusiva (no es el plan VIP)",
              de: "Exklusive Community (nicht VIP-Plan)",
              fr: "Communauté exclusive (pas le forfait VIP)",
              it: "Community esclusiva (non il piano VIP)",
              pt: "Comunidade exclusiva (não é o plano VIP)",
              nl: "Exclusieve community (niet het VIP-plan)",
            },
      };
    }
    case "custom":
    default:
      return {
        playbackId: "",
        ctaUrl: "",
        ctaLabel: {
          en: "Join",
          es: "Unirte",
          de: "Beitreten",
          fr: "Rejoindre",
          it: "Iscriviti",
          pt: "Participar",
          nl: "Deelnemen",
        },
      };
  }
}

export function getVideoCTAConfig(
  service: VideoCTAService,
  _countryCode?: string,
  _language: SupportedLanguage = "es"
): VideoCTAConfig {
  return getDefaultVideoConfig(service);
}

export function hasVideoConfigured(service: VideoCTAService): boolean {
  switch (service) {
    case "telegram":
    case "telegram_groups":
    case "channel_invite":
      return hasChannelInviteVideo();
    case "custom":
    default:
      return false;
  }
}

export async function getVideoCTAConfigWithPayment(
  service: VideoCTAService,
  _countryCode: string = "US",
  _language: SupportedLanguage = "es"
): Promise<VideoCTAConfig & { hasVideo: boolean }> {
  const config = getDefaultVideoConfig(service);
  const hasVideo = hasVideoConfigured(service);
  return { ...config, hasVideo };
}

export function getLocalizedLabel(
  config: VideoCTAConfig,
  language: SupportedLanguage = "es"
): string {
  if (config.ctaLabel) {
    return config.ctaLabel[language] || config.ctaLabel.en || "Join";
  }
  // Fallback translations
  const fallbacks: Record<SupportedLanguage, string> = {
    en: "Join",
    es: "Unirte",
    de: "Beitreten",
    fr: "Rejoindre",
    it: "Iscriviti",
    pt: "Participar",
    nl: "Deelnemen",
  };
  return fallbacks[language] || fallbacks.en;
}

export function getLocalizedTitle(
  config: VideoCTAConfig,
  language: SupportedLanguage = "es"
): string | undefined {
  if (config.title) {
    return config.title[language] || config.title.en;
  }
  return undefined;
}

export default {
  getVideoCTAConfig,
  getVideoCTAConfigWithPayment,
  getLocalizedLabel,
  getLocalizedTitle,
  getDefaultVideoConfig,
};
